import { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import {
  normalizeAvailableLanguages,
  normalizeLanguageCode,
} from "lib/language";

const DEFAULT_LANGUAGES = ["mn", "eng"];
const CACHED_LANGUAGES_KEY = "webinfo:languages";

const readCachedLanguages = () => {
  if (typeof window === "undefined") return [];
  try {
    const cached = localStorage.getItem(CACHED_LANGUAGES_KEY);
    const parsed = cached ? JSON.parse(cached) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCachedLanguages = (languages) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CACHED_LANGUAGES_KEY, JSON.stringify(languages));
  } catch {
    /* noop */
  }
};

export const useLanguage = (sourceInfo = null) => {
  const [cookies, setCookie] = useCookies(["language"]);
  const [availableLanguages, setAvailableLanguages] = useState(() => {
    const normalized = normalizeAvailableLanguages(sourceInfo);
    return normalized.length ? normalized : readCachedLanguages();
  });
  const [storedLanguage, setStoredLanguage] = useState("");

  // Memoize a stable key from sourceInfo to prevent infinite loops
  const sourceInfoKey = useMemo(() => {
    if (!sourceInfo) return "null";
    const source = sourceInfo.core || sourceInfo;
    return JSON.stringify({
      contentLanguages: source.contentLanguages,
      languages: source.languages,
    });
  }, [sourceInfo]);

  useEffect(() => {
    const normalized = normalizeAvailableLanguages(sourceInfo);
    if (normalized.length) {
      setAvailableLanguages(normalized);
      writeCachedLanguages(normalized);
      return;
    }

    const cached = readCachedLanguages();
    if (cached.length) {
      setAvailableLanguages(cached);
    }
  }, [sourceInfoKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setStoredLanguage(localStorage.getItem("language") || "");
    } catch {
      setStoredLanguage("");
    }
  }, []);

  const effectiveLanguages = useMemo(() => {
    return (
      availableLanguages && availableLanguages.length
        ? availableLanguages
        : DEFAULT_LANGUAGES
    ).map(normalizeLanguageCode);
  }, [availableLanguages]);

  // Memoize the default language from sourceInfo to create stable dependencies
  const sourceDefaultLanguage = useMemo(() => {
    const source = sourceInfo?.core || sourceInfo || {};
    return normalizeLanguageCode(
      source?.defaultLanguage || source?.translation?.language,
    );
  }, [sourceInfoKey, sourceInfo]);

  const resolveDefaultLanguage = useCallback(() => {
    if (
      sourceDefaultLanguage &&
      effectiveLanguages.includes(sourceDefaultLanguage)
    ) {
      return sourceDefaultLanguage;
    }
    if (effectiveLanguages.includes("eng")) return "eng";
    return effectiveLanguages[0] || DEFAULT_LANGUAGES[0];
  }, [effectiveLanguages, sourceDefaultLanguage]);

  const selectedLanguage = useMemo(() => {
    return (
      normalizeLanguageCode(cookies.language) ||
      normalizeLanguageCode(storedLanguage) ||
      resolveDefaultLanguage()
    );
  }, [cookies.language, resolveDefaultLanguage, storedLanguage]);

  useEffect(() => {
    const normalizedCookie = normalizeLanguageCode(cookies.language);
    const normalizedStored = normalizeLanguageCode(storedLanguage);
    const fallback = resolveDefaultLanguage();

    let next = normalizedCookie || normalizedStored || fallback;
    if (!next) return;

    if (!normalizedCookie || normalizedCookie === "undefined") {
      setCookie("language", next, { path: "/" });
    }

    if (!normalizedStored || normalizedStored === "undefined") {
      try {
        localStorage.setItem("language", next);
        setStoredLanguage(next);
      } catch {
        /* noop */
      }
    }

    if (!effectiveLanguages.includes(normalizeLanguageCode(next))) {
      next = fallback;
      if (next) {
        setCookie("language", next, { path: "/" });
        try {
          localStorage.setItem("language", next);
          setStoredLanguage(next);
        } catch {
          /* noop */
        }
      }
    }
  }, [
    cookies.language,
    effectiveLanguages,
    resolveDefaultLanguage,
    setCookie,
    storedLanguage,
  ]);

  const setLanguage = useCallback(
    (lang) => {
      const normalized = normalizeLanguageCode(lang);
      setCookie("language", normalized, { path: "/" });
      try {
        localStorage.setItem("language", normalized);
        setStoredLanguage(normalized);
      } catch {
        /* noop */
      }
    },
    [setCookie],
  );

  return {
    language: selectedLanguage,
    availableLanguages: effectiveLanguages,
    setLanguage,
  };
};
